import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import Header from "../../Header";
import Footer from "../../Footer";
import OwlCarousel from "react-owl-carousel";
import axios from "axios";
import "../GameDevelopment.css";
import "./MultiplayerGames.css";

export default function MultiplayerGames() {
    const [gameData, setGameData] = useState([]);

    useEffect(() => {
        axios.get("/games?filter[limit]=55&filter[where][is_online]=true&filter[where][is_deleted]=false&filter[where][is_active]=true&filter[order]=createdat%20DESC")
            .then((response) => {
                setGameData(Array.isArray(response.data) ? response.data : []);
            })
            .catch((error) => {
                console.log(error);
            });
    }, []);

    const getValidImagePath = (...images) => {
        const validImage = images.find(({ file }) => {
            if (typeof file !== "string") {
                return false;
            }

            const imageName = file.trim().toLowerCase();
            return imageName && imageName !== "null" && imageName !== "undefined";
        });

        return validImage ? `${validImage.folder}/${validImage.file.trim()}` : "";
    };

    const onDemandGames = [
        {
            title: "Card & Casino Games",
            description: "Rummy, Teen Patti, Poker, Callbreak, Spades, Blackjack, slots, roulette, and table-based multiplayer games.",
            items: ["Real-time tables", "Wallet flows", "Tournament logic"]
        },
        {
            title: "Board Games",
            description: "Ludo, Carrom, Chess, Snakes & Ladders, and custom board games with smooth turn-based or real-time play.",
            items: ["Private rooms", "Bot support", "Leaderboards"]
        },
        {
            title: "Sports & Fantasy Games",
            description: "Fantasy sports, prediction games, quiz leagues, sports contests, and live event-based multiplayer platforms.",
            items: ["Contest management", "Scoring systems", "Analytics"]
        },
        {
            title: "Casual Multiplayer Games",
            description: "Fast, social, and replayable multiplayer games for mobile and web audiences.",
            items: ["Quick matches", "Social invites", "Reward loops"]
        }
    ];

    const newGameCategories = [
        {
            title: "Real-Time PvP Games",
            description: "Action, racing, arcade, battle, shooter, and competitive games with low-latency real-time gameplay.",
            items: ["Matchmaking", "Lag handling", "Live rooms"]
        },
        {
            title: "Co-op & Social Games",
            description: "Collaborative gameplay, party games, team missions, social lobbies, chat, and friend-based game modes.",
            items: ["Team play", "Voice/chat ready", "Party systems"]
        },
        {
            title: "MMO & Persistent Worlds",
            description: "Scalable multiplayer worlds with player profiles, inventories, progression, economy, and live content systems.",
            items: ["Persistent data", "Economy systems", "Live events"]
        },
        {
            title: "Custom Multiplayer Concepts",
            description: "New IP, hybrid genres, experimental mechanics, and multiplayer products built from your unique idea.",
            items: ["Prototype first", "Custom rules", "Scalable roadmap"]
        }
    ];

    const platformFeatures = [
        "Matchmaking",
        "Game Rooms",
        "Private Tables",
        "Turn-Based Logic",
        "Real-Time Sync",
        "Leaderboards",
        "Wallet & Payments",
        "Admin Panel",
        "Bot Players",
        "Analytics",
        "Push Notifications",
        "LiveOps"
    ];

    const processSteps = [
        { step: "01", title: "Game Scope", description: "We define the category, rules, player flow, platforms, monetization, backend needs, and launch goals." },
        { step: "02", title: "Prototype", description: "We validate gameplay, matchmaking, room flow, UI screens, backend events, and core player actions." },
        { step: "03", title: "Production", description: "Our team builds gameplay, UI, multiplayer backend, admin tools, wallet flows, assets, and platform builds." },
        { step: "04", title: "Testing", description: "We test real-time behavior, edge cases, devices, payment flows, server load, reconnects, and release stability." },
        { step: "05", title: "Launch & Support", description: "We support publishing, monitoring, analytics, updates, bug fixes, monetization, and live game operations." }
    ];

    const faqs = [
        {
            question: "Can Oengines build on-demand multiplayer games?",
            answer: "Yes. We build on-demand multiplayer card, board, casino, casual, fantasy, and sports games with ready product flows and custom branding."
        },
        {
            question: "Can you build a completely new multiplayer game idea?",
            answer: "Yes. We can start from concept, prototype the core loop, define multiplayer architecture, and build the complete game for mobile, web, or PC."
        },
        {
            question: "Do you provide multiplayer backend development?",
            answer: "Yes. We build matchmaking, rooms, socket events, game servers, APIs, databases, admin panels, wallet systems, analytics, and live operations tools."
        },
        {
            question: "Which platforms do you support?",
            answer: "We support Android, iOS, WebGL, web platforms, PC, Unity, Unreal, Node.js backend, Firebase, cloud deployment, and store publishing."
        }
    ];

    const structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "Multiplayer Game Development Services",
        "provider": {
            "@type": "Organization",
            "name": "Oengines Games LLP",
            "url": "https://www.oengines.com"
        },
        "serviceType": "Multiplayer Game Development",
        "areaServed": "Worldwide",
        "description": "Multiplayer game development services for on-demand multiplayer games and new custom multiplayer game products.",
        "offers": [
            ...onDemandGames,
            ...newGameCategories
        ].map((item) => ({
            "@type": "Offer",
            "itemOffered": {
                "@type": "Service",
                "name": item.title,
                "description": item.description
            }
        }))
    };

    const publishedGamesSection = gameData.length ? (
        <section className="team-sec space new-space-bg game-store-section" id="Our_Games">
            <div className="container z-index-common">
                <div className="title-area text-center game-store-heading">
                    <span className="sub-title">OUR LIVE GAMES</span>
                    <h2 className="sec-title">Explore Our Published Games</h2>
                    <p>Browse selected games developed by Oengines and view their available store listings.</p>
                </div>
                <OwlCarousel
                    className="owl-theme game-store-carousel"
                    loop
                    margin={22}
                    nav
                    dots
                    responsive={{
                        0: { items: 1 },
                        576: { items: 2 },
                        992: { items: 3 },
                        1200: { items: 4 }
                    }}
                >
                    {gameData.map((gamedata, index) => {
                        const storeBackgroundImage = getValidImagePath(
                            { folder: "feature_graphics", file: gamedata.feature_graphics },
                            { folder: "feature_graphics", file: gamedata.feature_graphic },
                            { folder: "banners", file: gamedata.banner }
                        );
                        const storeBackgroundUrl = storeBackgroundImage ? `http://moregames.oengines.com/images/${storeBackgroundImage}` : "";

                        return (
                            <div className="game-store-slide" key={gamedata.id || gamedata.game_name || index}>
                                <article className="game-store-card">
                                    <div
                                        className={`game-store-icon${storeBackgroundUrl ? " has-store-bg" : ""}`}
                                        style={storeBackgroundUrl ? { backgroundImage: `linear-gradient(180deg, rgba(7, 19, 38, 0.12), rgba(7, 19, 38, 0.72)), url(${storeBackgroundUrl})` } : undefined}
                                    >
                                        <img src={`http://moregames.oengines.com/images/icons/${gamedata.icon}`} alt={`${gamedata.game_name} game icon`} />
                                    </div>
                                    <div className="game-store-content">
                                        <h3 className="game-store-title">{gamedata.game_name}</h3>
                                        <p className="game-store-description">{gamedata.short_description || "Available on selected app stores."}</p>
                                        <div className="game-store-links">
                                            {gamedata.android_link === "" ? "" :
                                                <a className="game-store-link" href={`${gamedata.android_link}`} target="_blank" rel="noreferrer" aria-label={`${gamedata.game_name} on Google Play`}>
                                                    <img src="assets/img/icon/play_store.png" alt="Get it on Google Play" className="playstore" />
                                                </a>
                                            }
                                            {gamedata.ios_link === "" ? "" :
                                                <a className="game-store-link" href={`${gamedata.ios_link}`} target="_blank" rel="noreferrer" aria-label={`${gamedata.game_name} on App Store`}>
                                                    <img src="assets/img/icon/app_store.png" alt="Download on the App Store" className="appstore" />
                                                </a>
                                            }
                                            {gamedata.android_link === "" && gamedata.ios_link === "" &&
                                                <span className="game-store-unavailable">Store link coming soon</span>
                                            }
                                        </div>
                                    </div>
                                </article>
                            </div>
                        );
                    })}
                </OwlCarousel>
            </div>
            <div className="shape-mockup" data-bottom="0" data-left="0">
                <div className="particle-2" id="particle-2"></div>
            </div>
        </section>
    ) : "";

    return (
        <>
            <Helmet>
                <title>Multiplayer Game Development Company | On-Demand & Custom Multiplayer Games | Oengines</title>
                <meta name="description" content="Oengines Games LLP builds on-demand and custom multiplayer games with matchmaking, game rooms, real-time backend, wallets, admin panels, analytics, QA, launch, and LiveOps support." />
                <meta name="keywords" content="multiplayer game development company, on demand multiplayer games, custom multiplayer game development, real time multiplayer game, card game development, board game development, multiplayer backend development" />
                <link rel="canonical" href="https://www.oengines.com/multiplayer-games" />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />
            </Helmet>
            <Header />
            <main className="multiplayer-page">
                <section className="multiplayer-hero">
                    <div className="container">
                        <div className="row align-items-center g-4">
                            <div className="col-lg-7">
                                <span className="multiplayer-eyebrow">Multiplayer Game Development</span>
                                <h1>Build On-Demand or New Multiplayer Games</h1>
                                <p>
                                    Oengines Games LLP builds scalable multiplayer games with real-time gameplay, matchmaking, game rooms,
                                    secure backend systems, wallet flows, admin panels, analytics, QA, publishing, and live operations.
                                </p>
                                <div className="multiplayer-hero-actions">
                                    <a href="/contact" className="as-btn">Start a Project <i className="fa-regular fa-arrow-right ms-2"></i></a>
                                    <a href="tel:+912614054978" className="multiplayer-call">Call: (0261) - 4054978</a>
                                </div>
                            </div>
                            <div className="col-lg-5">
                                <div className="multiplayer-hero-panel">
                                    <img src="assets/img/GameDevelopment/multiplayer_game.jpg" alt="Multiplayer game development" />
                                    <div className="multiplayer-hero-card">
                                        <strong>Real-Time Multiplayer</strong>
                                        <span>Rooms, matchmaking, backend, wallet, admin, analytics, and LiveOps.</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="multiplayer-category-section space">
                    <div className="container">
                        <div className="multiplayer-heading text-center">
                            <span>Choose Your Development Path</span>
                            <h2>On-Demand Game Products or New Multiplayer Concepts</h2>
                            <p>Start with a proven category or create a new multiplayer game from scratch. Both options can include custom UI, rules, backend, monetization, analytics, and publishing support.</p>
                        </div>
                        <div className="multiplayer-path-grid">
                            <article className="multiplayer-path-card">
                                <span className="multiplayer-path-label">Category 01</span>
                                <h3>On-Demand Multiplayer Games</h3>
                                <p>Best for businesses that want proven multiplayer game categories with faster planning, clear feature sets, and product-ready flows.</p>
                                <a href="#on-demand">View Categories</a>
                            </article>
                            <article className="multiplayer-path-card multiplayer-path-card--dark">
                                <span className="multiplayer-path-label">Category 02</span>
                                <h3>New Multiplayer Game Development</h3>
                                <p>Best for unique game concepts, new IP, hybrid genres, custom mechanics, competitive systems, or scalable multiplayer products.</p>
                                <a href="#new-games">Explore New Games</a>
                            </article>
                        </div>
                    </div>
                </section>

                <section className="multiplayer-list-section" id="on-demand">
                    <div className="container">
                        <div className="multiplayer-heading multiplayer-heading--split">
                            <div>
                                <span>On-Demand Multiplayer Games</span>
                                <h2>Ready Category Ideas with Custom Product Execution</h2>
                            </div>
                            <p>Use an established game category and customize the gameplay, branding, UI, backend, monetization, and admin tools for your business model.</p>
                        </div>
                        <div className="row g-4">
                            {onDemandGames.map((item) => (
                                <div className="col-lg-3 col-md-6" key={item.title}>
                                    <article className="multiplayer-service-card">
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                        <ul>
                                            {item.items.map((point) => <li key={point}>{point}</li>)}
                                        </ul>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="multiplayer-list-section multiplayer-list-section--light" id="new-games">
                    <div className="container">
                        <div className="multiplayer-heading multiplayer-heading--split">
                            <div>
                                <span>New Multiplayer Games</span>
                                <h2>Custom Multiplayer Products Built Around Your Idea</h2>
                            </div>
                            <p>For original concepts, we help shape the gameplay loop, architecture, prototype, production plan, platform build, testing, launch, and live support.</p>
                        </div>
                        <div className="row g-4">
                            {newGameCategories.map((item) => (
                                <div className="col-lg-3 col-md-6" key={item.title}>
                                    <article className="multiplayer-service-card multiplayer-service-card--light">
                                        <h3>{item.title}</h3>
                                        <p>{item.description}</p>
                                        <ul>
                                            {item.items.map((point) => <li key={point}>{point}</li>)}
                                        </ul>
                                    </article>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="multiplayer-feature-section space">
                    <div className="container">
                        <div className="multiplayer-feature-panel">
                            <div className="multiplayer-heading mb-0">
                                <span>Multiplayer Features</span>
                                <h2>Core Systems Your Multiplayer Game May Need</h2>
                                <p>We plan the technical architecture around gameplay type, latency needs, player volume, monetization model, and long-term operations.</p>
                            </div>
                            <div className="multiplayer-feature-list">
                                {platformFeatures.map((feature) => <span key={feature}>{feature}</span>)}
                            </div>
                        </div>
                    </div>
                </section>

                <div id="Our_Games"></div>
                {publishedGamesSection}

                <section className="multiplayer-process-section space">
                    <div className="container">
                        <div className="multiplayer-heading text-center">
                            <span>Development Process</span>
                            <h2>From Game Idea to Live Multiplayer Product</h2>
                        </div>
                        <div className="multiplayer-process-grid">
                            {processSteps.map((item) => (
                                <article className="multiplayer-process-card" key={item.step}>
                                    <span>{item.step}</span>
                                    <h3>{item.title}</h3>
                                    <p>{item.description}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="multiplayer-faq-section space">
                    <div className="container">
                        <div className="multiplayer-heading text-center">
                            <span>FAQ</span>
                            <h2>Multiplayer Game Development Questions</h2>
                        </div>
                        <div className="multiplayer-faq-grid">
                            {faqs.map((item) => (
                                <article className="multiplayer-faq-card" key={item.question}>
                                    <h3>{item.question}</h3>
                                    <p>{item.answer}</p>
                                </article>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="multiplayer-cta">
                    <div className="container">
                        <div className="multiplayer-cta-box">
                            <div>
                                <span>Ready to Build?</span>
                                <h2>Share your multiplayer game idea with Oengines.</h2>
                                <p>We can help you choose between an on-demand category or a new custom multiplayer product roadmap.</p>
                            </div>
                            <a href="/contact" className="as-btn">Book Consultation <i className="fa-regular fa-arrow-right ms-2"></i></a>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
}
